import React, { useState, useEffect, useRef } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';
import ColorPicker from '../ColorEditor/ColorPicker';
import FontPicker from '../FontPicker';
import BackgroundEditor from '../BackgroundEditor';
import FloatingLabelInput from '../../Custom/FloatingLabelInput';
import { fonts } from '../../Styles/fonts';
import reactCSS from 'reactcss';
import API from '../../../utils/API';
import { premium_themes } from '../../Styles/premiumThemes';
import { useAuth } from '../../../utils/Auth';
import ColorEditor from '../ColorEditor';
import { useNavigate, useLocation } from 'react-router';
import { useThemes } from '../../../utils/customHooks';
import './ThemeEditor.css';

const typeHelper = (url) => {
    switch (url.split(/\.(?=[^\.]+$)/)[1]) {
        case 'mp4':
            return 'video/mp4';
        case 'gif':
            return 'image/gif';
        case 'png':
            return 'image/png';
        case 'jpeg':
            return 'image/jpeg';
        case 'jpg':
            return 'image/jpg';
        default:
            return '';
    };
};

const ThemeEditor = (props) => {
    const { getUpdatedUserVcard, userObj, vcardObj } = useAuth();
    const themes = useThemes(userObj);
    const { custom_themes, profile_pic, name_first, name_last, tagline, job_title, organization_name, location } = vcardObj;
    const navigate = useNavigate();

    const themeTypeHelper = () => {
        switch(themeType) {
            case 'new':
                return {
                    id: custom_themes?.length ?? 0,
                    main_background: '#dddddd',
                    profile_img_border: '#ddd',
                    top_card_color: '#ffffff',
                    top_text_color: '#444',
                    btm_card_color: '#fff',
                    btm_text_color: '#444',
                    icon_color: '#eee',
                    btn_color: '#eee',
                    theme_name: themeName,
                    background: fileObj.preview,
                    font: font,
                    type: 'custom'
                };
            case 'default':
                return themes[themeId]
            case 'premium':
                return premium_themes[themeId];
            default:
                return custom_themes[themeId];
        };
    };

    const themeFileHelper = () => {
        switch(themeType) {
            case 'new':
                return {
                    background_updated: false,
                    file: '/images/blank-canvas.jpg',
                    motion: false,
                    preview: '/images/blank-canvas.jpg',
                    type: 'image/jpg',
                };
            case 'default':
                return {
                    background_updated: false,
                    file: themes[themeId].background,
                    motion: false,
                    preview: themes[themeId].background,
                    type: 'image/jpg',
                };
            case 'premium':
                return {
                    background_updated: false,
                    file: premium_themes[themeId].background,
                    motion: false,
                    preview: premium_themes[themeId].background,
                    type: 'image/jpg',
                };
            default:
                return {
                    background_updated: false,
                    file: custom_themes[themeId].background,
                    motion: custom_themes[themeId].background.includes('.mp4'),
                    preview: custom_themes[themeId].background,
                    type: typeHelper(custom_themes[themeId].background),
                };
        };
    };

    const editorRef = useRef(null);
    const paramsLocation = useLocation();
    const themeType = paramsLocation?.state?.themeType ?? 'new';
    const themeId = parseInt(paramsLocation?.state?.themeId) ?? 0;
    const deleteParam = paramsLocation?.state?.deleteParam ?? false;

    const [ showSpinner, setShowSpinner ] = useState(false);
    const [ oldBackground, setOldBackground ] = useState(undefined);
    const [ show, setShow ] = useState('design');
    const [ showPreview, setShowPreview ] = useState(false);
    const [ disableConfirm, setDisableConfirm ] = useState(themeType === 'new' ? true : false);
    const [ showColorEditor, setShowColorEditor ] = useState('');
    const [ showColorPicker, setShowColorPicker ] = useState('');
    const [ themeName, setThemeName ] = useState((themeType === 'custom') ? custom_themes[themeId].theme_name : `custom_theme_${Date.now().toString().slice(9)}`);
    const [ font, setFont ] = useState(themeType === 'custom' ? custom_themes[themeId].font : "'franklin_gothic_bookregular', sans-serif");
    const [ fileObj, setFileObj ] = useState(themeFileHelper());
    const [ themeEdit, setThemeEdit ] = useState(themeTypeHelper(themeId));
    
    useEffect(() => {
        if (themeType && vcardObj && custom_themes && themeId >= 0) {
            if (themeType === 'custom') {
                if (custom_themes[themeId]?.background) {
                    setOldBackground(custom_themes[themeId].background.split(`https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/`)[1]);
                } else {
                    setOldBackground(undefined);
                };
            };
        };
    }, [themeType, vcardObj, custom_themes, themeId]);

    useEffect(() => {
        setThemeEdit(prev => ({
            ...prev,
            theme_name: themeName,
            font: font,
            background: fileObj.preview
        }));
    }, [themeName, font, fileObj.preview]);

    useEffect(() => {
        const dbcPreview = document.querySelector('.theme-editor-inner-2');
        const btns = document.querySelector('.theme-editor-mobile-btns');

        if (showPreview) {
            dbcPreview?.classList.add('preview');
            btns?.classList.add('preview');
        } else {
            dbcPreview?.classList.remove('preview');
            btns?.classList.remove('preview');
        };
    }, [showPreview]);

    const styles = reactCSS({
        'default': {
            mainBGcolor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.main_background}`,
            },
            profileImgBorder: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.profile_img_border}`,
            },
            topCardColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.top_card_color}`,
            },
            topTextColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.top_text_color}`,
            },
            btmCardColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.btm_card_color}`,
            },
            btmTextColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.btm_text_color}`,
            },
            iconColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.icon_color}`,
            },
            btnColor: {
                height: '1.5rem',
                width: '5rem',
                border: '1px solid #495057',
                borderRadius: '1.75rem',
                background: `${themeEdit.btn_color}`,
            },
            swatch: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '.5rem',
                height: '3rem',
                width: '7rem',
                background: '#ddd',
                border: 'none',
                borderRadius: '3rem',
                cursor: 'pointer',
                boxShadow: 'inset -4px -4px 6px #bbb, inset 4px 4px 6px #fff, 2px 2px 5px #bbb'
            },
            popover: {
                position: 'absolute',
                zIndex: '10',
            },
            cover: {
                position: 'fixed',
                top: '0px',
                right: '0px',
                bottom: '0px',
                left: '0px',
            },
        }
    });

    const handleClose = () => setShowColorPicker('');

    const handleChange = (e, name) => {
        if (e.target) {
            const value = e.target.value;
            setThemeEdit(theme => ({
                ...theme,
                theme_name: value
            }));
        } else {
            setThemeEdit({
                ...themeEdit,
                [name]: e
            });
        };
    };

    const handleResetColors = () => {
        if (themeType === 'new') {
            setThemeEdit(prev => ({
                ...prev,
                main_background: '#dddddd',
                profile_img_border: '#ddd',
                top_card_color: '#ffffff',
                top_text_color: '#444',
                btm_card_color: '#ffffff',
                btm_text_color: '#444',
                icon_color: '#eee',
                btn_color: '#eee',
            }));
        } else if (themeType === 'default') {
            const prevColors = themes[themeId];
            setThemeEdit(prev => ({
                ...prev,
                main_background: prevColors.main_background,
                profile_img_border: prevColors.profile_img_border,
                top_card_color: prevColors.top_card_color,
                top_text_color: prevColors.top_text_color,
                btm_card_color: prevColors.btm_card_color,
                btm_text_color: prevColors.btm_text_color,
                icon_color: prevColors.icon_color,
                btn_color: prevColors.btn_color,
            }));
        } else if (themeType === 'premium') {
            const prevColors = premium_themes[themeId];
            setThemeEdit(prev => ({
                ...prev,
                main_background: prevColors.main_background,
                profile_img_border: prevColors.profile_img_border,
                top_card_color: prevColors.top_card_color,
                top_text_color: prevColors.top_text_color,
                btm_card_color: prevColors.btm_card_color,
                btm_text_color: prevColors.btm_text_color,
                icon_color: prevColors.icon_color,
                btn_color: prevColors.btn_color,
            }));
        } else {
            const prevColors = custom_themes[themeId];
            setThemeEdit(prev => ({
                ...prev,
                main_background: prevColors.main_background,
                profile_img_border: prevColors.profile_img_border,
                top_card_color: prevColors.top_card_color,
                top_text_color: prevColors.top_text_color,
                btm_card_color: prevColors.btm_card_color,
                btm_text_color: prevColors.btm_text_color,
                icon_color: prevColors.icon_color,
                btn_color: prevColors.btn_color,
            }));
        };
    };

    const handleBackgroundPreview = () => {
        if (fileObj.motion) {
            return (
                <div className='preview-theme-card-background-wrapper'>
                    <div className='preview-theme-card-background'>
                        <video src={themeEdit.background} autoPlay playsInline loop muted className='preview-theme-card-video-background' style={{ zIndex: '4' }} />
                        <div className='preview-theme-card-video-background-blur-wrapper'>
                            <video src={themeEdit.background} autoPlay playsInline loop muted className='preview-theme-card-video-background-blur' />
                        </div>
                        {profile_pic &&
                            <div className='preview-theme-card-profile-pic-wrap' style={{ borderColor: themeEdit.profile_img_border }}>
                                <img src={`${profile_pic}?${Date.now()}`} alt='profile' height='100%' width='100%'></img>
                            </div>
                        }
                    </div>
                </div>
            );
        } else {
            return (
                <div className='preview-theme-card-background-wrapper'>
                    <div className='preview-theme-card-background'>
                        <img src={themeEdit.background} className='preview-theme-card-background-img' alt='Top Card Background' ></img>
                        {profile_pic &&
                            <div className='preview-theme-card-profile-pic-wrap' style={{ borderColor: themeEdit.profile_img_border }}>
                                <img src={`${profile_pic}?${Date.now()}`} alt='profile' height='100%' width='100%'></img>
                            </div>
                        }
                    </div>
                </div>
            );
        };
    };

    // handle background file and theme save
    const handleBackgroundFile = () => {
        setDisableConfirm(true);

        const stillFiles = ['image/png', 'image/jpeg', 'image/jpg'];
        const motionFiles = ['image/gif', 'video/mp4'];
        
        if (fileObj?.background_updated) {
            if (stillFiles.includes(fileObj.type) && editorRef.current) {
                editorRef.current.getImageScaledToCanvas().toBlob(blob => {
                    const newFile = new File([blob], 'image', { type: blob.type });
                    if (deleteParam) {
                        handleThemeReplace(newFile);
                    } else {
                        handleTheme(newFile);
                    };
                });
            } else if (motionFiles.includes(fileObj.type)) {
                if (typeof fileObj.file === 'string') {
                    handleTheme(undefined);
                } else {
                    if (deleteParam) {
                        handleThemeReplace(fileObj.file);
                    } else {
                        handleTheme(fileObj.file);
                    };
                };
            };
        } else {
            handleTheme(false);
        };
    };

    const handleTheme = async (bgFile) => {
        let backgroundName;

        const customThemes = custom_themes ? custom_themes : [];
        const theme = themeEdit;
        theme.type = 'custom';

        if (bgFile) {
            backgroundName = `${Date.now().toString()}.${bgFile.type.slice(bgFile.type.indexOf('/') + 1)}`;
            theme.background = `https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${userObj?.uid}/${backgroundName}`;
        } else {
            theme.background = fileObj.file;
        };

        if (themeType === 'new') {
            customThemes.push(theme);
        } else if (themeType === 'default' || themeType === 'premium') {
            theme.id = customThemes.length;
            customThemes.push(theme);
        } else {
            customThemes[theme.id] = theme;
        };

        let userEdit = {
            custom_themes: customThemes,
            profile_theme: theme
        };

        try {
            setShowSpinner(true);

            if (bgFile) {
                await API.updateBackground(userObj?.uid, bgFile, backgroundName, oldBackground);
                await API.updateVcardObject(userEdit);
            } else {
                await API.updateVcardObject(userEdit);
            };

            await getUpdatedUserVcard();
            window.location.assign('/settings');
        } catch (err) {
            console.log('User information not updated', err);
        } finally {
            setShowSpinner(false);
        };
    };

    const handleThemeReplace = async (bgFile) => {
        const id = vcardObj.profile_theme.id;
        const customThemes = custom_themes ? custom_themes : [];
        const theme = themeEdit;
        const backgroundName = `${Date.now().toString()}.${bgFile.type.slice(bgFile.type.indexOf('/') + 1)}`;

        theme.type = 'custom';
        theme.background = `https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${userObj?.uid}/${backgroundName}`;
        theme.id = id;

        customThemes[id] = theme;

        const userEdit = {
            custom_themes: customThemes,
            profile_theme: theme
        };

        try {
            setShowSpinner(true);
            await API.updateBackground(userObj?.uid, bgFile, backgroundName, oldBackground)
            // await API.updateVcardObject(userEdit);
            // await getUpdatedUserVcard();
            // setShowSpinner(false);
            // window.location.assign('/settings');
        } catch (err) {
            console.log('User information not updated', err);
        };
    };

    if (!paramsLocation?.state) {
        if (custom_themes?.length < 5) {
            navigate('/settings/themes/editor', { state: { themeType: 'new', themeId: '0', delete: false }});
            return <></>
        } else {
            navigate('/settings/themes');
            return <></>
        }
    } else {
        return (
            <div className='theme-editor-wrapper'>
                <div className='theme-editor-container'>
                    <div className='theme-editor-title'>
                        Theme Editor
                        {(themeType === 'new' || themeType === 'default') ?
                            <div className='theme-editor-subtitle'>Customize your new design below. Express yourself and get creative!</div>
                            :
                            <div className='theme-editor-subtitle'>Edit your design below. Make it unique!</div>
                        }
                    </div>
                    <div className='theme-editor-inner'>
                        <div className='theme-editor-inner-1'>
                            <div className='theme-editor-tabs'>
                                <div className={show === 'design' ? 'theme-editor-tab' : 'theme-editor-tab inactive-1'} onClick={() => setShow('design')}>Design</div>
                                <div className={show === 'background' ? 'theme-editor-tab' : 'theme-editor-tab inactive-2'} onClick={() => setShow('background')}>Background</div>
                            </div>
                            <div className='theme-editor-design' style={show === 'design' ? { zIndex: '5' } : { zIndex: '1' }}>
                                {!showColorEditor ?
                                    <>
                                        <InputGroup>
                                            <FloatingLabelInput
                                                state={themeName}
                                                setState={setThemeName}
                                                type='text'
                                                id='theme-name'
                                                label="Theme Name"
                                                placeholder="Give your theme a name!"
                                                required={true}
                                                style={{ borderRadius: '2.5rem', marginBottom: '.5rem' }}
                                            />
                                        </InputGroup>
                                        <InputGroup className='theme-editor-font-wrap mb-3'>
                                            <label className='theme-editor-font-label'>Font</label>
                                            <FontPicker fonts={fonts} font={font} setFont={setFont} />
                                        </InputGroup>
                                        <div className='theme-editor-swatch-container'>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorEditor('main_background')}>
                                                    <div style={styles.mainBGcolor} />
                                                </div>
                                                <div className='theme-editor-label'>Main Background</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorPicker('profile_img_border')}>
                                                    <div style={styles.profileImgBorder} />
                                                </div>
                                                {showColorPicker === 'profile_img_border' ?
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={handleClose} />
                                                        <ColorPicker
                                                            name='profile_img_border'
                                                            color={themeEdit.profile_img_border}
                                                            onChange={handleChange}
                                                        />
                                                    </div> : null
                                                }
                                                <div className='theme-editor-label'>Profile Image Border</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorEditor('top_card_color')}>
                                                    <div style={styles.topCardColor} />
                                                </div>
                                                <div className='theme-editor-label'>Top Card</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorPicker('top_text_color')}>
                                                    <div style={styles.topTextColor} />
                                                </div>
                                                {showColorPicker === 'top_text_color' ?
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={handleClose} />
                                                        <ColorPicker
                                                            name='top_text_color'
                                                            color={themeEdit.top_text_color}
                                                            onChange={handleChange}
                                                        />
                                                    </div> : null
                                                }
                                                <div className='theme-editor-label'>Top Text</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorEditor('btm_card_color')}>
                                                    <div style={styles.btmCardColor} />
                                                </div>
                                                <div className='theme-editor-label'>Bottom Card</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorPicker('btm_text_color')}>
                                                    <div style={styles.btmTextColor} />
                                                </div>
                                                {showColorPicker === 'btm_text_color' ?
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={handleClose} />
                                                        <ColorPicker
                                                            name='btm_text_color'
                                                            color={themeEdit.btm_text_color}
                                                            onChange={handleChange}
                                                        />
                                                    </div> : null
                                                }
                                                <div className='theme-editor-label'>Bottom Text</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorEditor('icon_color')}>
                                                    <div style={styles.iconColor} />
                                                </div>
                                                <div className='theme-editor-label'>Icon Color</div>
                                            </div>
                                            <div className='theme-editor-swatches'>
                                                <div style={styles.swatch} className='theme-editor-swatch' onClick={() => setShowColorPicker('btn_color')}>
                                                    <div style={styles.btnColor} />
                                                </div>
                                                {showColorPicker === 'btn_color' ?
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={handleClose} />
                                                        <ColorPicker
                                                            name='btn_color'
                                                            color={themeEdit.btn_color}
                                                            onChange={handleChange}
                                                        />
                                                    </div> : null
                                                }
                                                <div className='theme-editor-label'>Button Color</div>
                                            </div>
                                        </div>
                                        <div className='theme-editor-reset'>
                                            <button onClick={handleResetColors}>Reset Colors</button>
                                        </div>
                                    </>
                                    :
                                    <ColorEditor styles={styles} showColorEditor={showColorEditor} setShowColorEditor={setShowColorEditor} themeEdit={themeEdit} setThemeEdit={setThemeEdit} />
                                }
                            </div>
                            <div className='theme-editor-background' style={show === 'background' ? { zIndex: '5' } : { zIndex: '1' }}>
                                <BackgroundEditor editor={editorRef} fileObj={fileObj} setFileObj={setFileObj} permissions={userObj.permissions} setDisableConfirm={setDisableConfirm} themeType={themeType} />
                            </div>
                        </div>
                        {showPreview === false ?
                            <div className='theme-editor-mobile-btns'>
                                <button className='theme-editor-preview-btn' onClick={() => setShowPreview(!showPreview)}>{showPreview ? 'Close' : 'Preview'}</button>
                                <button className='theme-editor-save-btn' disabled={disableConfirm || themeName.length < 1 || showSpinner} onClick={handleBackgroundFile}>{disableConfirm ? <i className='far fa-spinner fa-spin' /> : 'Save'}</button>
                            </div>
                            :
                            null
                        }
                        <div className='theme-editor-inner-2' style={(window.innerWidth <= 500 && showPreview === true) ? { background: themeEdit.main_background } : null}>
                            <div className='preview-theme-card' style={{ background: themeEdit.main_background }}>
                                <div className='preview-theme-card-top-card' style={{ background: themeEdit.top_card_color, fontFamily: themeEdit.font }}>
                                    {handleBackgroundPreview()}
                                    <div className='preview-theme-card-top-card-info' style={{ color: themeEdit.top_text_color }}>
                                        {name_first && name_last ? <div className='preview-theme-card-top-card-name'>{name_first} {name_last}</div>
                                            : (name_first && !name_last) ? <div className='preview-theme-card-top-card-name'>{name_first}</div>
                                                : (!name_first && name_last) ? <div className='preview-theme-card-top-card-name'>{name_last}</div>
                                                    : null
                                        }
                                        {tagline ?
                                            <div className='preview-theme-card-top-card-tagline'>{tagline}</div>
                                            : null
                                        }
                                        <div className='preview-theme-top-card-info-inner' style={{ color: themeEdit.top_text_color }}>
                                            {(job_title && organization_name) ?
                                                <p>{job_title} at {organization_name}</p>
                                                : (job_title && !organization_name) ?
                                                    <p>{job_title}</p>
                                                    : (!job_title && organization_name) &&
                                                    <p>{organization_name}</p>
                                            }
                                            {location &&
                                                <p>{location}</p>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className='preview-theme-card-bottom-card' style={{ background: themeEdit.btm_card_color }}>
                                    <div className='preview-theme-card-top-btns'>
                                        <div style={{ background: themeEdit.btn_color, color: themeEdit.btm_text_color, borderColor: themeEdit.card_background, fontFamily: themeEdit.font }}><i className="far fa-circle-down me-1" style={{ fontSize: '.9rem' }} />Download</div>
                                        <div style={{ background: themeEdit.btn_color, color: themeEdit.btm_text_color, borderColor: themeEdit.card_background, fontFamily: themeEdit.font }}><i className='fas fa-user-plus' /></div>
                                        <div style={{ background: themeEdit.btn_color, color: themeEdit.btm_text_color, borderColor: themeEdit.card_background, fontFamily: themeEdit.font }}><i className='fas fa-envelope' /></div>
                                    </div>
                                    <div className='preview-theme-card-btns'>
                                        <div className='preview-theme-card-btn' style={{ background: `linear-gradient(to right, transparent 8%, ${themeEdit.btn_color} 20%, ${themeEdit.btn_color})` }}>
                                            <div className='preview-theme-card-icon'>
                                                <i className='fas fa-phone' style={{ background: themeEdit.icon_color }} />
                                            </div>
                                            <div className='preview-theme-card-btn-words' style={{ color: themeEdit.btm_text_color, fontFamily: themeEdit.font }}>
                                                <div style={{ letterSpacing: '1px' }}>Phone</div>
                                                <div>555-555-5555</div>
                                            </div>
                                            <div className='preview-theme-card-arrow' style={{ color: themeEdit.btm_text_color }}>
                                                <i className='fas fa-arrow-right' />
                                            </div>
                                        </div>
                                        <div className='preview-theme-card-btn' style={{ background: `linear-gradient(to right, transparent 8%, ${themeEdit.btn_color} 20%, ${themeEdit.btn_color})` }}>
                                            <div className='preview-theme-card-icon'>
                                                <i className='fab fa-chrome' style={{ background: themeEdit.icon_color }} />
                                            </div>
                                            <div className='preview-theme-card-btn-words' style={{ color: themeEdit.btm_text_color, fontFamily: themeEdit.font }}>
                                                <div style={{ letterSpacing: '1px' }}>Website</div>
                                                <div>www.example.com</div>
                                            </div>
                                            <div className='preview-theme-card-arrow' style={{ color: themeEdit.btm_text_color }}>
                                                <i className='fas fa-arrow-right' />
                                            </div>
                                        </div>
                                        <div className='preview-theme-card-btn' style={{ background: `linear-gradient(to right, transparent 8%, ${themeEdit.btn_color} 20%, ${themeEdit.btn_color})` }}>
                                            <div className='preview-theme-card-icon'>
                                                <i className='fas fa-envelope' style={{ background: themeEdit.icon_color }} />
                                            </div>
                                            <div className='preview-theme-card-btn-words' style={{ color: themeEdit.btm_text_color, fontFamily: themeEdit.font }}>
                                                <div style={{ letterSpacing: '1px' }}>Email</div>
                                                <div>example@example.com</div>
                                            </div>
                                            <div className='preview-theme-card-arrow' style={{ color: themeEdit.btm_text_color }}>
                                                <i className='fas fa-arrow-right' />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {(showPreview === true && window.innerWidth <= 500) ?
                                    <div className='theme-editor-mobile-btns'>
                                        <button className='theme-editor-preview-btn' onClick={() => setShowPreview(!showPreview)}>{showPreview ? 'Close' : 'Preview'}</button>
                                        <button className='theme-editor-save-btn' disabled={disableConfirm} onClick={handleBackgroundFile}>{disableConfirm ? <i className='far fa-spinner fa-spin' /> : 'Save'}</button>
                                    </div>
                                    :
                                    null
                                }
                            </div>
                            {(showPreview === true && (window.innerWidth > 500 && window.innerWidth <= 768)) ?
                                <div className='theme-editor-mobile-btns'>
                                    <button className='theme-editor-preview-btn' onClick={() => setShowPreview(!showPreview)}>{showPreview ? 'Close' : 'Preview'}</button>
                                    <button className='theme-editor-save-btn' disabled={disableConfirm} onClick={handleBackgroundFile}>{disableConfirm ? <i className='far fa-spinner fa-spin' /> : 'Save'}</button>
                                </div>
                                :
                                null
                            }
                            {window.innerWidth > 768 ? <button className='theme-editor-save-btn' disabled={disableConfirm} onClick={handleBackgroundFile}>{disableConfirm ? <i className='far fa-spinner fa-spin' /> : 'Save'}</button> : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default ThemeEditor;